import React, {useEffect, useRef} from 'react'
import ApexCharts, {ApexOptions} from 'apexcharts'
import {getCSS, getCSSVariableValue} from '../../../../_metronic/assets/ts/_utils'
import {useShortScale} from '../../../hooks/useShortScale'

type StatisticsData = {
  [key: string]: number
}
type Props = {
  className: string
  color: string
  label: string
  statisticsData: StatisticsData
}

const StatisticsWidget: React.FC<Props> = ({className, color, statisticsData, label}) => {
  const {convertValueToShortScale} = useShortScale()

  const chartRef = useRef<HTMLDivElement | null>(null)
  let maxValue: number
  let allZeroes = true
  if (statisticsData) {
    maxValue = Math.max(...Object.values(statisticsData))
    const values = Object.values(statisticsData).map(Number)
    allZeroes = values.every((val) => val === 0)
  }

  const refreshChart = () => {
    if (!chartRef.current) {
      return
    }

    const height = parseInt(getCSS(chartRef.current, 'height'))
    const labelColor = getCSSVariableValue('--kt-gray-800')
    const baseColor = getCSSVariableValue('--kt-' + color)
    const lightColor = getCSSVariableValue('--kt-' + color + '-light')

    const chart = new ApexCharts(
      chartRef.current,
      getChartOptions(
        height,
        labelColor,
        baseColor,
        lightColor,
        statisticsData,
        maxValue,
        label,
        convertValueToShortScale
      )
    )
    if (chart) {
      chart.render()
    }

    return chart
  }

  useEffect(() => {
    const chart = refreshChart()

    return () => {
      if (chart) {
        chart.destroy()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartRef, statisticsData])

  return (
    <div className={`${allZeroes ? '' : ''} ${className}`}>
      <div className='d-flex flex-column p-0'>
        <div
          ref={chartRef}
          className='statistics-widget-3-chart card-rounded-bottom'
          style={{height: '168px'}}
        ></div>
      </div>
    </div>
  )
}

export {StatisticsWidget}

function getChartOptions(
  height: number,
  labelColor: string,
  baseColor: string,
  lightColor: string,
  statisticsData: any,
  maxValue: number,
  label: string,
  convertValueToShortScale: any
): ApexOptions {
  const options: ApexOptions = {
    series: [
      {
        name: '',
        data: statisticsData &&
          typeof statisticsData === 'object' && [
            Number(Object.values(statisticsData)[0]),
            Number(Object.values(statisticsData)[1]),
            Number(Object.values(statisticsData)[2]),
            Number(Object.values(statisticsData)[3]),
            Number(Object.values(statisticsData)[4]),
            Number(Object.values(statisticsData)[5]),
            Number(Object.values(statisticsData)[6]),
          ],
      },
    ],
    chart: {
      fontFamily: 'inherit',
      type: 'area',
      height: height,
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: false,
      },
      sparkline: {
        enabled: true,
      },
    },
    plotOptions: {},
    legend: {
      show: false,
    },
    dataLabels: {
      enabled: false,
    },
    fill: {
      type: 'solid',
      opacity: 1,
    },
    stroke: {
      curve: 'smooth',
      show: true,
      width: 3,
      colors: [baseColor],
    },
    xaxis: {
      categories: statisticsData && [
        Object.keys(statisticsData)[0],
        Object.keys(statisticsData)[1],
        Object.keys(statisticsData)[2],
        Object.keys(statisticsData)[3],
        Object.keys(statisticsData)[4],
        Object.keys(statisticsData)[5],
        Object.keys(statisticsData)[6],
      ],
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      labels: {
        show: false,
        style: {
          colors: labelColor,
          fontSize: '12px',
        },
      },
      crosshairs: {
        show: false,
        position: 'front',
        stroke: {
          color: '#E4E6EF',
          width: 1,
          dashArray: 3,
        },
      },
      tooltip: {
        enabled: false,
      },
    },
    yaxis: {
      min: 0,
      max: maxValue,
      labels: {
        show: false,
        style: {
          colors: labelColor,
          fontSize: '12px',
        },
      },
    },
    states: {
      normal: {
        filter: {
          type: 'none',
          value: undefined,
        },
      },
      hover: {
        filter: {
          type: 'none',
          value: undefined,
        },
      },
      active: {
        allowMultipleDataPointsSelection: false,
        filter: {
          type: 'none',
          value: undefined,
        },
      },
    },
    tooltip: {
      style: {
        fontSize: '12px',
      },
      y: {
        formatter: function (val) {
          if (label === 'capital') {
            return convertValueToShortScale(val) + ' ' + label
          } else {
            return val + ' ' + label
          }
        },
      },
    },
    colors: [lightColor],
    markers: {
      colors: [lightColor],
      strokeColors: [baseColor],
      strokeWidth: 3,
    },
  }
  return options
}
